import { useRef, type HTMLAttributes, type ReactNode } from "react";

import {
  useEscapeDismiss,
  useFocusRestore,
  useFocusTrap,
  useScrollLock,
} from "./layerUtils";

type DrawerSize = "md" | "lg";

function classes(base: string[], className: string | undefined): string {
  if (className) {
    base.push(className);
  }

  return base.join(" ");
}

export interface DrawerProps extends HTMLAttributes<HTMLDivElement> {
  /** Accessible name for the dialog panel. */
  ariaLabel: string;
  children: ReactNode;
  isOpen: boolean;
  onDismiss: () => void;
  /** Panel width: `md` ≈ 480px, `lg` ≈ 640px (UI-LAW §4a: 420–640px). */
  size?: DrawerSize;
}

/**
 * Drawer — UI-LAW §4a layer for inspecting or editing a related object
 * WITHOUT leaving list/page context: row detail + history, medium forms
 * (6–12 fields), docs/09-mandated detail surfaces. Right-anchored,
 * full-height, over a scroll-locked but still-readable page (scrim opacity
 * stays low). The drawer subject MUST be URL-addressable by the CALLER via
 * a search param (e.g. `?key=<id>`) so refresh restores it — this component
 * only renders the layer. Composition law: one drawer at a time, never
 * stacked.
 */
export function Drawer({
  ariaLabel,
  children,
  className,
  isOpen,
  onDismiss,
  size = "md",
  ...props
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Order matters: capture the previously focused element (restore) before
  // the trap moves focus into the panel.
  useFocusRestore(isOpen);
  useFocusTrap(panelRef, isOpen);
  useScrollLock(isOpen);
  useEscapeDismiss(onDismiss, isOpen);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={classes(
        ["marker-drawer", `marker-drawer--${size}`],
        className,
      )}
      role="presentation"
      {...props}
    >
      <button
        aria-label="Close drawer"
        className="marker-drawer__scrim"
        onClick={onDismiss}
        type="button"
      />
      <div
        aria-label={ariaLabel}
        aria-modal="true"
        className="marker-drawer__panel"
        ref={panelRef}
        role="dialog"
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}
